<template>
  <div>
    <transition :name="slideDirection" mode="out-in">
      <v-row
        v-if="currentSlide == 0"
        key="0"
        no-gutters
        class="py-3 word-break px-5 d-flex flex-column align-center mb-8"
      >
        <v-card-title class="word-break">Link a Bank Account</v-card-title>
        <caption class="grey--text mt-2">
          You'll be redirected to securely connect your bank account through Stripe.
        </caption>

        <v-btn
          color="brandCyan"
          depressed
          class="mt-6 white--text"
          width="240"
          :loading="loading"
          @click="startSetupIntent"
          >Connect Bank Account</v-btn
        >
      </v-row>
      <v-row
        v-else-if="currentSlide == 1"
        key="1"
        no-gutters
        class="py-3 word-break px-5 d-flex flex-column align-center mb-8"
      >
        <v-card-title>Processing...</v-card-title>
        <Robin :showText="false" :width="130" :height="130" animation="flying" :loop="true" />
      </v-row>
      <v-row
        v-else-if="currentSlide == 2"
        key="2"
        no-gutters
        class="py-3 word-break px-5 d-flex flex-column align-center mb-8"
      >
        <v-card-title>All done!</v-card-title>
        <caption class="grey--text mt-2">
          Your bank account has been successfully connected!
        </caption>

        <Robin :showText="false" :width="130" :height="130" animation="backflip" :loop="false" />
        <v-btn
          color="brandCyan"
          depressed
          class="mt-4 white--text"
          width="140"
          :disabled="submitting"
          @click="onSuccess"
          >Done</v-btn
        >
      </v-row>
      <v-row
        v-else-if="currentSlide == 3"
        key="3"
        no-gutters
        class="py-3 word-break px-5 d-flex flex-column align-center mb-8 align-text-left"
      >
        <v-card-title class="title-text word-break full-width">{{
          errorMessage ? errorMessage : "There was an error setting up your bank account"
        }}</v-card-title>

        <CustomerServiceContact layout="stacked" class="pl-4 full-width align-text-left" />
        <div class="d-flex mt-4">
          <v-btn
            color="brandCyan"
            depressed
            class="mr-4 white--text"
            width="150"
            @click="(currentSlide = clientSecret ? 4 : 0), (errorMessage = null)"
            >Try Again</v-btn
          >
          <v-btn color="brandCyan" class="white--text" depressed width="150" @click="resetForm"
            >Done</v-btn
          >
        </div>
      </v-row>
      <v-form
        v-else-if="currentSlide == 4"
        key="4"
        ref="verifyForm"
        v-model="verifyFormValid"
        class="full-width px-5 py-3"
        @submit.prevent="verifyFormValid && verifyMicrodeposit()"
      >
        <v-card-title class="word-break pl-0">Verify Your Bank Account</v-card-title>
        <p class="grey--text">
          Stripe sent a small deposit to your bank account. Please enter the 6-digit code (starts
          with SM) found in the statement descriptor of that deposit.
        </p>
        <v-text-field
          v-model="descriptorCode"
          label="6-digit descriptor code"
          placeholder="SM11AA"
          outlined
          dense
          color="brandCyan"
          class="mt-4"
          :rules="[
            (v) => !!v || 'Code is required',
            (v) => !!(v && v.length === 6) || 'Code must be exactly 6 characters',
            (v) => !!(v && /^SM/i.test(v)) || 'Code should start with SM',
          ]"
        />
        <v-btn
          color="brandCyan"
          depressed
          class="mt-2 white--text"
          width="160"
          :disabled="!verifyFormValid"
          :loading="verifying"
          @click="verifyMicrodeposit"
          >Verify</v-btn
        >
        <v-btn text class="mt-2 ml-2" @click="resetForm">Cancel</v-btn>
      </v-form>
    </transition>
  </div>
</template>

<script>
import PaymentService from "@/services/PaymentService";
import Robin from "@/components/Robin";
import CustomerServiceContact from "@/components/help/CustomerServiceContact";

import { mapState } from "vuex";

export default {
  name: "StripeBankAccount",
  props: {
    token: String,
    verifySetupIntentId: {
      type: String,
      default: null,
    },
  },
  components: {
    Robin,
    CustomerServiceContact,
  },
  data() {
    return {
      currentSlide: 0,
      slideDirection: "topic-left",
      stripe: null,
      loading: false,
      errorMessage: null,
      clientSecret: null,
      descriptorCode: null,
      verifyFormValid: false,
      verifying: false,
      submitting: false,
    };
  },
  created() {
    console.log("Created stripe bank widget");
  },
  mounted() {
    if (!this.isScriptLoaded("//js.stripe.com/v3/")) {
      this.includeStripe(
        "js.stripe.com/v3/",
        function () {
          this.configureStripe();
          this.checkVerifyMode();
        }.bind(this)
      );
    } else {
      this.configureStripe();
      this.checkVerifyMode();
    }
  },
  beforeUnmount() {},
  methods: {
    isScriptLoaded(src) {
      return Boolean(document.querySelector('script[src="' + src + '"]'));
    },
    includeStripe(URL, callback) {
      let documentTag = document,
        tag = "script",
        object = documentTag.createElement(tag),
        scriptTag = documentTag.getElementsByTagName(tag)[0];
      object.src = "//" + URL;
      if (callback) {
        object.addEventListener(
          "load",
          function (e) {
            callback(null, e);
          },
          false
        );
      }
      scriptTag.parentNode.insertBefore(object, scriptTag);
    },
    configureStripe() {
      this.stripe = Stripe(process.env.VUE_APP_STRIPE_PUBLIC_KEY); //eslint-disable-line
    },
    async startSetupIntent() {
      this.loading = true;
      this.errorMessage = null;

      try {
        const setupResp = await PaymentService.createSetupIntent(this.magicLinkToken);

        if (setupResp.error) {
          this.errorMessage = setupResp.error;
          this.currentSlide = 3;
          this.loading = false;
          return;
        }

        const { client_secret } = setupResp;

        const { setupIntent, error } = await this.stripe.collectBankAccountForSetup({
          clientSecret: client_secret,
          params: {
            payment_method_type: "us_bank_account",
            payment_method_data: {
              billing_details: {
                name: this.userProfile.displayName,
                email: this.userProfile.businessEmail,
              },
            },
          },
          expand: ["payment_method"],
        });

        if (error) {
          console.log("Financial Connections error: ", error);
          if (error.type === "validation_error" || error.code === "user_abort") {
            this.loading = false;
            return;
          }
          this.errorMessage = error.message;
          this.currentSlide = 3;
          this.loading = false;
          return;
        }

        if (setupIntent.status === "requires_payment_method") {
          this.loading = false;
          this.currentSlide = 0;
          return;
        } else if (setupIntent.status === "requires_confirmation") {
          this.currentSlide = 1;
          const { error: confirmError } = await this.stripe.confirmUsBankAccountSetup(
            client_secret
          );

          if (confirmError) {
            console.log("Confirm error: ", confirmError);
            this.errorMessage = confirmError.message;
            this.currentSlide = 3;
            this.loading = false;
            return;
          }

          this.currentSlide = 2;
        } else if (setupIntent.status === "succeeded") {
          this.currentSlide = 2;
        } else if (setupIntent.status === "requires_action") {
          this.clientSecret = client_secret;
          this.$emit("pending-verification", setupResp.setup_intent_id);
          this.currentSlide = 4;
        } else {
          this.errorMessage = "Unexpected status: " + setupIntent.status;
          this.currentSlide = 3;
        }
      } catch (e) {
        console.log("Error in SetupIntent flow: ", e);
        this.errorMessage = null;
        this.currentSlide = 3;
      } finally {
        this.loading = false;
      }
    },
    async checkVerifyMode() {
      if (!this.verifySetupIntentId) return;
      this.loading = true;
      try {
        const resp = await PaymentService.getSetupIntent(
          this.verifySetupIntentId,
          this.magicLinkToken
        );
        if (resp.error) {
          this.errorMessage = resp.error;
          this.currentSlide = 3;
        } else {
          this.clientSecret = resp.client_secret;
          this.currentSlide = 4;
        }
      } catch (e) {
        this.errorMessage = null;
        this.currentSlide = 3;
      } finally {
        this.loading = false;
      }
    },
    async verifyMicrodeposit() {
      this.verifying = true;
      try {
        const { setupIntent, error } = await this.stripe.verifyMicrodepositsForSetup(
          this.clientSecret,
          { descriptor_code: this.descriptorCode.toUpperCase() }
        );

        if (error) {
          this.errorMessage = error.message;
          this.currentSlide = 3;
          return;
        }

        if (setupIntent.status === "succeeded") {
          this.currentSlide = 2;
        } else {
          this.errorMessage = "Verification incomplete. Status: " + setupIntent.status;
          this.currentSlide = 3;
        }
      } catch (e) {
        console.log("Error verifying microdeposit: ", e);
        this.errorMessage = null;
        this.currentSlide = 3;
      } finally {
        this.verifying = false;
      }
    },
    resetForm() {
      this.$emit("scrollToTop");
      this.$emit("reset");
    },
    onSuccess() {
      if (this.submitting) return;
      this.submitting = true;
      this.$emit("scrollToTop");
      this.$emit("bank-added");
    },
    previousSlide() {
      this.resetForm();
    },
  },
  computed: {
    ...mapState(["userProfile", "magicLinkToken"]),
  },
};
</script>

<style scoped id="styles">
.topic-left-enter {
  opacity: 0;
  transform: translateX(100%);
}

.topic-right-enter {
  opacity: 0;
  transform: translateX(-100%);
}

.topic-left-enter-active,
.topic-right-enter-active {
  transition: all 0.65s cubic-bezier(0.19, 1, 0.22, 1);
}

.wallet-icon {
  background: #549c2d;
  color: #fff;
  padding: 6px;
  border-radius: 4px;
  font-size: 1.3em;
}
</style>
